import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Header from '../home/components/Header';
import Footer from '../home/components/Footer';
import WhatsAppButton from '../../components/WhatsAppButton';
import SEOHead from '../../components/SEOHead';
import blogPosts from '../../mocks/blogPosts';

export default function Blog() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState('all');
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://www.robatemizlik.com';

  const categories = [
    { id: 'all', name: 'Tümü' },
    { id: 'tips', name: 'Temizlik İpuçları' },
    { id: 'industry', name: 'Sektör Haberleri' },
    { id: 'health', name: 'Sağlık & Hijyen' },
    { id: 'technology', name: 'Teknoloji' }
  ];

  const filteredPosts = selectedCategory === 'all' 
    ? blogPosts 
    : blogPosts.filter(post => post.category === selectedCategory);

  const handlePostClick = (id: string) => {
    navigate(`/blog/${id}`);
  };

  const schemaData = {
    "@context": "https://schema.org",
    "@type": "Blog",
    "@id": `${siteUrl}/blog#blog`,
    "url": `${siteUrl}/blog`,
    "name": "Roba Temizlik Blog",
    "description": "Profesyonel temizlik hizmetleri, hijyen ipuçları ve sektör haberleri hakkında güncel bilgiler",
    "publisher": {
      "@type": "Organization",
      "name": "Roba Temizlik",
      "url": siteUrl
    },
    "blogPost": blogPosts.map(post => ({
      "@type": "BlogPosting",
      "@id": `${siteUrl}/blog/${post.id}`,
      "headline": post.title,
      "description": post.excerpt,
      "datePublished": post.date,
      "author": {
        "@type": "Person",
        "name": post.author
      },
      "image": post.image
    }))
  };

  return (
    <>
      <SEOHead
        title="Blog - Roba Temizlik | Temizlik İpuçları ve Sektör Haberleri"
        description="Profesyonel temizlik hizmetleri, hijyen ipuçları ve sektör haberleri hakkında güncel bilgiler. Roba Temizlik uzmanlarından temizlik tavsiyeleri ve en iyi uygulamalar."
        keywords="temizlik ipuçları, hijyen tavsiyeleri, temizlik sektörü, profesyonel temizlik, istanbul temizlik"
        canonical={`${siteUrl}/blog`}
        ogType="website"
        ogTitle="Blog - Roba Temizlik | Temizlik İpuçları ve Sektör Haberleri"
        ogDescription="Profesyonel temizlik hizmetleri, hijyen ipuçları ve sektör haberleri hakkında güncel bilgiler."
        ogUrl={`${siteUrl}/blog`}
        ogImage={blogPosts[0]?.image}
        twitterTitle="Blog - Roba Temizlik | Temizlik İpuçları ve Sektör Haberleri"
        twitterDescription="Profesyonel temizlik hizmetleri, hijyen ipuçları ve sektör haberleri hakkında güncel bilgiler."
        twitterImage={blogPosts[0]?.image}
        schemaData={schemaData}
      />
      <Header />
      
      <main className="pt-20">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-teal-50 to-white py-16">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <div className="text-center max-w-3xl mx-auto">
              <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
                {t('blog.title')}
              </h1>
              <p className="text-xl text-gray-600">
                {t('blog.subtitle')}
              </p>
            </div>
          </div>
        </section>

        {/* Categories */}
        <section className="bg-white border-b border-gray-200 sticky top-20 z-10">
          <div className="max-w-7xl mx-auto px-6 lg:px-12 py-6">
            <div className="flex flex-wrap gap-3 justify-center">
              {categories.map(category => (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  className={`px-6 py-2 rounded-full font-medium transition-all whitespace-nowrap cursor-pointer ${
                    selectedCategory === category.id
                      ? 'bg-teal-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {category.name}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Blog Posts Grid */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredPosts.map(post => (
                <article 
                  key={post.id}
                  onClick={() => handlePostClick(post.id)}
                  className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 cursor-pointer group"
                >
                  <div className="relative w-full h-64 overflow-hidden">
                    <img
                      src={post.image}
                      alt={post.title}
                      className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute top-4 left-4">
                      <span className="bg-teal-600 text-white px-4 py-1 rounded-full text-sm font-medium">
                        {categories.find(c => c.id === post.category)?.name}
                      </span>
                    </div>
                  </div>
                  
                  <div className="p-6">
                    <div className="flex items-center gap-4 text-sm text-gray-500 mb-3">
                      <div className="flex items-center gap-2">
                        <i className="ri-calendar-line"></i>
                        <span>{post.date}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <i className="ri-time-line"></i>
                        <span>{post.readTime}</span>
                      </div>
                    </div>
                    
                    <h2 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-teal-600 transition-colors line-clamp-2">
                      {post.title}
                    </h2>
                    
                    <p className="text-gray-600 mb-4 line-clamp-3">
                      {post.excerpt}
                    </p>
                    
                    <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-teal-100 rounded-full flex items-center justify-center">
                          <i className="ri-user-line text-teal-600"></i>
                        </div>
                        <span className="text-sm font-medium text-gray-700">{post.author}</span>
                      </div>
                      
                      <div className="flex items-center gap-2 text-teal-600 font-medium group-hover:gap-3 transition-all">
                        <span className="text-sm">Devamını Oku</span>
                        <i className="ri-arrow-right-line"></i>
                      </div>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
      <WhatsAppButton />
    </>
  );
}
